import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Assignment,
  Person,
  CalendarToday,
  AccessTime,
  AttachMoney,
} from '@mui/icons-material';
import { getServiceBookingById } from '../../../services/Api';
import { useCallback } from 'react';

const ViewServiceBookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBookingDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getServiceBookingById(id);

      if (response.success) {
        setBooking(response.data);
      } else {
        setError(response?.message || 'Failed to fetch booking details');
      }
    } catch (err) {
      console.error('Error fetching booking details:', err);
      setError(err.response?.data?.message || 'Failed to fetch booking details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchBookingDetails();
    }
  }, [id, fetchBookingDetails]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'secondary';
      case 'out_for_delivery': return 'primary';
      case 'delivered': return 'success';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'pending': return 'warning';
      case 'success': return 'success';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={() => navigate('/bookings/service-bookings')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 300 }}>
            Booking Details
          </Typography>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">{error || 'Booking not found'}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/bookings/service-bookings')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 300 }}>
              Booking Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {booking.bookingNumber || `#${booking.id}`}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Booking Information */}
        <Grid size={{ xs: 12, md: 12 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment />
              Booking Information
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 4 }}>
                <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {booking.bookingNumber || `#${booking.id}`}
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={booking.bookingStatus || 'N/A'}
                  size="small"
                  color={getStatusColor(booking.bookingStatus)}
                  sx={{ fontWeight: 500 }}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                <Chip
                  label={booking.paymentStatus || 'N/A'}
                  size="small"
                  color={getPaymentStatusColor(booking.paymentStatus)}
                  sx={{ fontWeight: 500 }}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday sx={{ fontSize: 16 }} />
                  {booking.date ? new Date(booking.date).toLocaleDateString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="body2" color="text.secondary">Time Slot</Typography>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime sx={{ fontSize: 16 }} />
                  {booking.timeSlot || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              Customer Information
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {booking.user?.name || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary">Mobile</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {booking.user?.mobile || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">
                  {booking.user?.email || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {booking.address ? `${booking.address.street}, ${booking.address.area}, ${booking.address.city}, ${booking.address.state} - ${booking.address.pincode}` : 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">Partner</Typography>
                <Typography variant="body1">
                  {booking.partner?.name || booking.partnerId ?
                    (booking.partner?.name || `Partner ID: ${booking.partnerId}`) :
                    '-'
                  }
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Services */}
        <Grid size={{ xs: 6 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Booked Services
            </Typography>

            {booking.items && booking.items.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {booking.items.map((item, index) => (
                  <Box key={item.id} sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    border: '1px solid',
                    borderColor: 'text.border',
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {item.serviceOptionName || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {item.categoryName || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Size: {item.size || 'Standard'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.quantity || 1}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ₹{item.totalPrice || item.unitPrice || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No services booked
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Pricing Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney />
              Pricing Details
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body1">₹{booking.subtotal || '0.00'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Service Charge</Typography>
                <Typography variant="body1">₹{booking.serviceCharge || '0.00'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">GST Amount</Typography>
                <Typography variant="body1">₹{booking.gstAmount || '0.00'}</Typography>
              </Box>
              {booking.appCouponDiscount && booking.appCouponDiscount !== '0.00' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Coupon Discount</Typography>
                  <Typography variant="body1" color="success.main">
                    -₹{booking.appCouponDiscount}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  ₹{booking.totalAmount || '0.00'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Additional Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Additional Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Created At</Typography>
                <Typography variant="body1">
                  {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Updated At</Typography>
                <Typography variant="body1">
                  {booking.updatedAt ? new Date(booking.updatedAt).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
              <Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Coupon Code</Typography>
                <Typography variant="body1">
                  {booking.appCouponCode || 'Not Applied'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Rating</Typography>
                <Typography variant="body1">
                  {booking.rating || 'Not Rated'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewServiceBookingDetails;